/**
 * @file Section footer de la barre de navigation verticale : avatar utilisateur, notifications et company switcher.
 * @module layouts/app/nav-footer-section
 *
 * Rend deux variantes selon la prop `isMini` :
 * - Mode mini : icônes compactes empilées verticalement
 * - Mode normal : carte utilisateur complète avec nom, avatar, notifications et company switcher
 */

import React from 'react';
import PropTypes from 'prop-types';
import {m} from 'framer-motion';
import {Box, Stack, Avatar, Divider, Typography, IconButton} from '@mui/material';
import {useSession, useActiveCompany} from 'packages/common/hooks/session';
import CompanySwitcher from 'packages/common/components/company-switcher/CompanySwitcher';
import {usePopover} from 'packages/common/components/custom-popover';
import UserAccountPopover from './user-account-popover';
import NotificationsPopover from 'packages/common/layouts/_common/notifications-popover';

/**
 * Section footer de la barre de navigation latérale.
 *
 * @param {Object} props
 * @param {boolean} [props.isMini=false] - Active le rendu compact (icônes seules, sans texte ni company switcher)
 * @return {JSX.Element}
 */
export default function NavFooterSection({isMini = false}) {
  const {user} = useSession();
  const {hasMultipleCompanies} = useActiveCompany();
  const popover = usePopover();

  if (isMini) {
    return (
      <>
        <Divider sx={{borderStyle: 'dashed', mb: 1.5, borderColor: 'rgba(255, 255, 255, 0.12)'}} />

        <Stack spacing={1} alignItems="center" sx={{mb: 2, px: 1}}>
          <NotificationsPopover />

          <IconButton
            onClick={popover.onOpen}
            sx={{
              'p': 0.5,
              'border': (theme) => `2px solid rgba(255, 255, 255, 0.2)`,
              'bgcolor': 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                borderColor: (theme) => theme.palette.primary.light,
              },
            }}
          >
            <Avatar
              src={user?.photoURL}
              alt={`${user?.firstName} ${user?.lastName}`}
              sx={{
                width: 32,
                height: 32,
                bgcolor: (theme) => theme.palette.primary.main,
                color: 'white',
                fontWeight: 600,
              }}
            >
              {user?.firstName?.charAt(0)?.toUpperCase() || ''}
            </Avatar>
          </IconButton>
        </Stack>

        <UserAccountPopover
          popover={popover}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}
          transformOrigin={{vertical: 'bottom', horizontal: 'center'}}
        />
      </>
    );
  }

  return (
    <>
      <Divider sx={{borderStyle: 'dashed', mb: 2, borderColor: 'rgba(255, 255, 255, 0.12)'}} />

      <Box
        component={m.div}
        whileHover={{y: -2}}
        whileTap={{y: 0}}
        onClick={popover.onOpen}
        sx={{
          'mx': 1.5,
          'mb': 2,
          'p': 1.5,
          'cursor': 'pointer',
          'borderRadius': 2,
          'transition': 'all 0.2s',
          '&:hover': {
            bgcolor: 'rgba(255, 255, 255, 0.08)',
          },
        }}
      >
        <Stack spacing={1.25} sx={{justifyContent: 'center'}}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{minWidth: 0}}>
            <Avatar
              src={user?.photoURL}
              alt={`${user?.firstName} ${user?.lastName}`}
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                fontSize: '0.75rem',
                bgcolor: (theme) => theme.palette.primary.main,
                color: 'white',
                fontWeight: 600,
              }}
            >
              {user?.firstName?.charAt(0)?.toUpperCase() || ''}
            </Avatar>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#E2E8F0',
              }}
            >
              {user ? `${user.firstName} ${user.lastName}` : 'Loading...'}
            </Typography>
            {/* stopPropagation : évite d'ouvrir le popover utilisateur en cliquant sur les notifications */}
            <Box onClick={(e) => e.stopPropagation()}>
              <NotificationsPopover />
            </Box>
          </Stack>
          {hasMultipleCompanies && (
            <Box onClick={(e) => e.stopPropagation()} sx={{minWidth: 0, width: '100%'}}>
              <CompanySwitcher variant="compact" size="small" />
            </Box>
          )}
        </Stack>
      </Box>

      <UserAccountPopover
        popover={popover}
        anchorOrigin={{vertical: 'top', horizontal: 'left'}}
        transformOrigin={{vertical: 'bottom', horizontal: 'left'}}
      />
    </>
  );
}

NavFooterSection.propTypes = {
  isMini: PropTypes.bool,
};
